import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Tag, Button, Input, Select, Row, Col, Tooltip, Empty, Progress, Alert,
} from 'antd';
import { PlusOutlined, ReloadOutlined, FolderOpenOutlined, RightOutlined, FileTextOutlined, CloseOutlined, DollarOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import TableExportButton from '../../components/TableExportButton';
import axiosInstance from '../../variables/api/axiosInstance';
import {
  formatDate, formatMontant,
  STATUTS_DOSSIER_RECLAMATION, STATUT_DOSSIER_MAP,
} from '../../variables/constants';
import NouveauDossierModal from './NouveauDossierModal';
import PageHeader from '../../components/PageHeader';

import { useTranslation, Trans } from 'react-i18next';
import { localeIntl } from '../../i18n/format';

const { Search } = Input;
const { Option } = Select;

const GREEN = '#1a6b3c';

/* Étape réelle d'un dossier, calculée par le serveur (EtapeReclamation) : pièces,
   consolidation, offre, paiement. Le statut du dossier ne vaut jamais que « Ouvert »
   ou « Clôturé » : une colonne de « Ouvert » ne disait pas où en est chaque victime. */
const COULEUR_ETAPE = {
  PIECES: 'gold',
  CONSOLIDATION: 'purple',
  MUR: 'green',
  OFFRE_PREPARATION: 'blue',
  OFFRE_VALIDEE: 'blue',
  OFFRE_HOMOLOGUE: 'geekblue',
  REPONSE_HOMOLOGUE: 'geekblue',
  OFFRE_ENVOYEE: 'geekblue',
  OFFRE_REJETEE: 'red',
  // TE : offre de l'homologue, compagnie, chèque, quittance
  OFFRE_RECUE_HOMOLOGUE: 'blue',
  OFFRE_COMPAGNIE: 'geekblue',
  REPONSE_COMPAGNIE: 'geekblue',
  CHEQUE_RECU: 'lime',
  CHEQUE_TRANSMIS: 'lime',
  QUITTANCE: 'success',
  ACCORD: 'cyan',
  PAIEMENT: 'lime',
  PAYE: 'success',
  CLOTURE: 'default',
};

const TagEtape = ({ dossier, style }) => {
  const { t } = useTranslation('reclamations');
  const pieces = { recues: dossier.nbPiecesObligatoiresRecues ?? 0, requises: dossier.nbPiecesRequises ?? 0 };
  return (
  <Tooltip title={dossier.etape === 'PIECES' || dossier.etape === 'CONSOLIDATION'
    ? (dossier.consolidationRequise ? (dossier.estConsolidee ? t('liste.etape.piecesVictimeConsolidee', pieces) : t('liste.etape.piecesConsolidationAttendue', pieces)) : t('liste.etape.pieces', pieces))
    : null}>
    <Tag color={COULEUR_ETAPE[dossier.etape] || 'default'} style={{ borderRadius: 20, fontSize: 11, margin: 0, ...style }}>
      {dossier.etapeLibelle || STATUT_DOSSIER_MAP[dossier.statut]?.label || dossier.statut}
    </Tag>
  </Tooltip>
);

};

// Seuls ces deux statuts sont jamais posés sur un dossier : les autres ne filtraient rien.
const STATUTS_FILTRABLES = STATUTS_DOSSIER_RECLAMATION.filter((s) => ['OUVERT', 'CLOTURE'].includes(s.value));

const MAX_VICTIMES_LIGNE = 3;

export default function ListReclamations() {
  const { t } = useTranslation('reclamations');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sinistreIdParam = searchParams.get('sinistreId');
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [search, setSearch] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [defaultSinistreId, setDefaultSinistreId] = useState(null);
  const [defaultVictimeId, setDefaultVictimeId] = useState(null);

  /* Arrivée depuis un sinistre (« Gérer les pièces ») : on montre SES dossiers. La page
     ouvrait d'office « Ouvrir un dossier » sur la liste de tous les dossiers, même quand
     chaque victime avait déjà le sien - et choisir l'une d'elles finissait en erreur.
     Les personnes encore sans dossier sont signalées, avec de quoi l'ouvrir. */
  const [sinistreFiltre, setSinistreFiltre] = useState(null); // { id, numero, sansDossier }
  useEffect(() => {
    if (!sinistreIdParam) { setSinistreFiltre(null); return; }
    let annule = false;
    Promise.all([
      axiosInstance.get(`/v1/sinistres/${sinistreIdParam}`),
      axiosInstance.get(`/v1/sinistres/${sinistreIdParam}/maturite`).catch(() => null),
    ]).then(([s, m]) => {
      if (annule) return;
      setSinistreFiltre({
        id: sinistreIdParam,
        numero: s.data?.data?.numeroSinistreLocal,
        sansDossier: (m?.data?.data?.victimes || []).filter(v => !v.dossierTrackingId),
      });
    }).catch(() => { if (!annule) setSinistreFiltre({ id: sinistreIdParam, numero: null, sansDossier: [] }); });
    return () => { annule = true; };
  }, [sinistreIdParam]);

  // La recherche part au serveur : filtrer la seule page chargée (20 dossiers) ne
  // trouvait pas une victime rangée plus loin.
  const [recherche, setRecherche] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setRecherche(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (page = 0) => {
    if (sinistreIdParam && !sinistreFiltre) return; // on attend le numéro du sinistre
    setLoading(true);
    try {
      const parSinistre = !!sinistreFiltre?.numero;
      const params = new URLSearchParams({
        page: parSinistre ? 0 : page, size: parSinistre ? 100 : 20,
        ...(filtreStatut && { statut: filtreStatut }),
        ...((recherche || parSinistre) && { search: recherche || sinistreFiltre.numero }),
      });
      const res = await axiosInstance.get(`/v1/dossiers-reclamation?${params}`);
      const body = res.data || {};
      const d = body.data;
      let list = Array.isArray(d) ? d : (d?.content || []);
      // La recherche du serveur est un « contient » : « 2026/0363 » ramènerait aussi « 2026/03631 ».
      if (sinistreFiltre) list = list.filter(x => x.sinistreTrackingId === sinistreFiltre.id);
      const total = sinistreFiltre ? list.length : (body.totalElements ?? d?.totalElements ?? list.length);
      setDossiers(list);
      setPagination(prev => ({ ...prev, total, ...(page === 0 && { current: 1 }) }));
    } catch {
      setDossiers([]);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut, recherche, sinistreIdParam, sinistreFiltre]);

  useEffect(() => { load(0); }, [load]);

  const voirTousLesDossiers = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('sinistreId');
    setSearchParams(next, { replace: true });
  };

  const filtered = dossiers;

  // Regroupement par sinistre - un sinistre peut héberger plusieurs dossiers
  // (un par victime). On affiche 1 ligne mère par sinistre + lignes filles dépliables.
  const grouped = (() => {
    const map = {};
    for (const d of filtered) {
      const key = d.sinistreTrackingId || d.numeroSinistreLocal || `_${d.dossierTrackingId}`;
      if (!map[key]) {
        map[key] = {
          key, sinistreTrackingId: d.sinistreTrackingId,
          numeroSinistreLocal: d.numeroSinistreLocal || t('liste.sansSinistre'),
          dossiers: [], totalReclame: 0, totalRetenu: 0,
        };
      }
      map[key].dossiers.push(d);
      map[key].totalReclame += Number(d.montantTotalReclame) || 0;
      map[key].totalRetenu += Number(d.montantTotalRetenu) || 0;
    }
    return Object.values(map).sort((a, b) => (b.totalReclame || 0) - (a.totalReclame || 0));
  })();

  // Colonnes de la table mère (1 ligne = 1 sinistre)
  const colsSinistre = [
    {
      title: t('liste.colonnes.numeroSinistre'), dataIndex: 'numeroSinistreLocal', key: 'sin', width: 200, fixed: 'left',
      render: (v, r) => r.sinistreTrackingId ? (
        <button
          onClick={() => navigate(`/sinistres/${r.sinistreTrackingId}`)}
          style={{ background: 'none', border: 'none', color: GREEN, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 13 }}
        >{v}</button>
      ) : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{v}</span>,
    },
    {
      title: t('liste.colonnes.dossiers'), key: 'nb', width: 110, align: 'center',
      render: (_, r) => (
        <Tag color="blue" style={{ borderRadius: 20, fontWeight: 600 }}>
          {t('liste.nbDossiers', { count: r.dossiers.length })}
        </Tag>
      ),
    },
    {
      title: t('commun.totalReclame'), key: 'totRecl', width: 150, align: 'right',
      render: (_, r) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{formatMontant(r.totalReclame)}</span>,
    },
    {
      title: t('commun.totalRetenu'), key: 'totRet', width: 150, align: 'right',
      render: (_, r) => {
        const pct = r.totalReclame > 0 ? Math.round((r.totalRetenu / r.totalReclame) * 100) : 0;
        return (
          <div>
            <div style={{ fontFamily: 'var(--font-main)', color: GREEN, fontWeight: 700 }}>
              {formatMontant(r.totalRetenu)}
            </div>
            <Progress percent={pct} size="small" showInfo={false} strokeColor={GREEN} />
          </div>
        );
      },
    },
    {
      /* Une ligne par victime : son nom et l'étape où elle en est. Au-delà de trois,
         le détail se lit en dépliant le sinistre. */
      title: t('liste.colonnes.ouEnEst'), key: 'etapes',
      render: (_, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {r.dossiers.slice(0, MAX_VICTIMES_LIGNE).map((d) => (
            <div key={d.dossierTrackingId} style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <span style={{ fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}>
                {d.victimeNomComplet || d.numeroDossier}
              </span>
              <TagEtape dossier={d} style={{ fontSize: 10.5 }} />
            </div>
          ))}
          {r.dossiers.length > MAX_VICTIMES_LIGNE && (
            <span style={{ fontSize: 11.5, color: '#6b7280' }}>
              {t('liste.autresVictimes', { count: r.dossiers.length - MAX_VICTIMES_LIGNE })}
            </span>
          )}
        </div>
      ),
    },
  ];

  // Colonnes de la sous-table (dossiers d'un sinistre). On entre dans un dossier par
  // le nom de la victime : « REC-2026-00004 » ne disait pas de qui il s'agit. Le numéro
  // reste lisible dessous, et le sinistre est déjà la ligne mère.
  const columns = [
    {
      title: t('commun.victime'), dataIndex: 'victimeNomComplet', key: 'vict', width: 260, fixed: 'left',
      render: (v, r) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
          <button
            onClick={() => navigate(`/reclamations/${r.dossierTrackingId}`)}
            style={{ background: 'none', border: 'none', color: GREEN, fontWeight: 700, cursor: 'pointer', padding: 0, fontSize: 13, textAlign: 'left' }}
          >{v || r.numeroDossier}</button>
          {v && <span style={{ fontSize: 11, color: '#9ca3af' }}>{r.numeroDossier}</span>}
        </div>
      ),
    },
    {
      title: t('liste.colonnes.etape'), dataIndex: 'etapeLibelle', key: 'etape', width: 190,
      render: (_, r) => <TagEtape dossier={r} />,
    },
    {
      title: t('commun.reclame'), dataIndex: 'montantTotalReclame', key: 'recl', width: 130, align: 'right',
      format: 'currency', numeric: true,
      render: (v) => <span style={{ fontSize: 12, fontFamily: 'var(--font-main)' }}>{formatMontant(v)}</span>,
    },
    {
      title: t('commun.retenu'), dataIndex: 'montantTotalRetenu', key: 'ret', width: 130, align: 'right',
      format: 'currency', numeric: true,
      render: (v, r) => {
        const pct = r.montantTotalReclame > 0
          ? Math.round((v / r.montantTotalReclame) * 100)
          : 0;
        return (
          <div>
            <div style={{ fontSize: 12, fontFamily: 'var(--font-main)', color: GREEN, fontWeight: 600 }}>
              {formatMontant(v)}
            </div>
            <Progress
              percent={pct} size="small" showInfo={false}
              strokeColor={GREEN} style={{ marginTop: 2 }}
            />
          </div>
        );
      },
    },
    {
      title: t('commun.pieces'), key: 'nb', width: 110, align: 'center',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
          <Tooltip title={t('commun.piecesAdministratives')}>
            <Tag color="blue" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
              <FileTextOutlined /> {r.nbPiecesAdmin ?? 0}
            </Tag>
          </Tooltip>
          <Tooltip title={t('commun.factures')}>
            <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 11, margin: 0 }}>
              <DollarOutlined /> {r.nbFactures ?? 0}
            </Tag>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t('liste.colonnes.ouvertLe'), dataIndex: 'dateOuverture', key: 'date', width: 110, align: 'center',
      format: 'date',
      render: (v) => <span style={{ fontSize: 12, color: '#6b7280' }}>{formatDate(v)}</span>,
    },
  ];

  // L'export est à plat (une ligne par dossier) : il garde le sinistre et le numéro.
  const colonnesExport = [
    { title: t('liste.colonnes.sinistre'), dataIndex: 'numeroSinistreLocal' },
    { title: t('commun.victime'), dataIndex: 'victimeNomComplet' },
    { title: t('liste.colonnes.numeroDossier'), dataIndex: 'numeroDossier' },
    { title: t('liste.colonnes.etape'), dataIndex: 'etapeLibelle', export: (v, r) => v || STATUT_DOSSIER_MAP[r.statut]?.label || '' },
    { title: t('commun.reclame'), dataIndex: 'montantTotalReclame', format: 'currency', numeric: true },
    { title: t('commun.retenu'), dataIndex: 'montantTotalRetenu', format: 'currency', numeric: true },
    { title: t('liste.colonnes.piecesRecues'), dataIndex: 'nbPiecesAdmin', numeric: true },
    { title: t('commun.factures'), dataIndex: 'nbFactures', numeric: true },
    { title: t('liste.colonnes.ouvertLe'), dataIndex: 'dateOuverture', format: 'date' },
  ];

  return (
    <Layout title={t('liste.titreOnglet')}>
      {/* Le tableau affiche `grouped` : une ligne = un sinistre, pas un dossier.
          Le compte annonce doit rester celui des dossiers, d'ou `pagination.total`
          (totalElements du backend, toutes pages confondues). */}
      <PageHeader
        titre={t('liste.titre')}
        icone={<FolderOpenOutlined />}
        compte={pagination.total}
        unite={t('liste.unite')}
        actions={(
          <Button
            type="primary" icon={<PlusOutlined />}
            style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}
            onClick={() => setModalOpen(true)}
          >
            {t('liste.ouvrirDossier')}
          </Button>
        )}
      />

      {sinistreIdParam && (
        <Alert
          type={sinistreFiltre?.sansDossier?.length ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16, borderRadius: 10 }}
          message={(
            <span style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span><Trans t={t} i18nKey="liste.filtreSinistre.titre" values={{ numero: sinistreFiltre?.numero || '…' }} components={{ 1: <strong /> }} /></span>
              <Button size="small" icon={<CloseOutlined />} onClick={voirTousLesDossiers} style={{ borderRadius: 6 }}>
                {t('liste.filtreSinistre.voirTous')}
              </Button>
            </span>
          )}
          description={sinistreFiltre?.sansDossier?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              <span>{t('liste.filtreSinistre.sansDossier')}</span>
              {sinistreFiltre.sansDossier.map(v => (
                <span key={v.victimeTrackingId} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <strong>{v.victimeNomComplet || t('liste.filtreSinistre.victimeSansNom')}</strong>
                  {v.estAdversaire && <Tag style={{ borderRadius: 20, margin: 0 }}>{t('liste.filtreSinistre.partieAdverse')}</Tag>}
                  <Button size="small" type="primary" icon={<PlusOutlined />}
                    style={{ background: GREEN, borderColor: GREEN, borderRadius: 6 }}
                    onClick={() => { setDefaultSinistreId(sinistreIdParam); setDefaultVictimeId(v.victimeTrackingId); setModalOpen(true); }}>
                    {t('liste.filtreSinistre.ouvrirSonDossier')}
                  </Button>
                </span>
              ))}
            </div>
          )}
        />
      )}

      <div className="cb-card" style={{ marginBottom: 20 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={12}>
            <Search
              placeholder={t('liste.filtres.recherche')}
              value={search} onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              placeholder={t('liste.filtres.tousStatuts')} style={{ width: '100%' }}
              value={filtreStatut || undefined} allowClear
              onChange={(v) => setFiltreStatut(v || '')}
            >
              {STATUTS_FILTRABLES.map(s => (
                <Option key={s.value} value={s.value}>
                  <Tag color={s.color} style={{ borderRadius: 20 }}>{s.label}</Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={4} style={{ display: 'flex', gap: 8 }}>
            <Tooltip title={t('commun.actualiser')}>
              <Button icon={<ReloadOutlined />} onClick={() => load(0)} />
            </Tooltip>
            <TableExportButton
              floating
              data={filtered}
              columns={colonnesExport}
              filename="dossiers-reclamation"
              title={t('liste.titre')}
              subtitle={[
                filtreStatut && t('liste.export.statut', { statut: STATUT_DOSSIER_MAP[filtreStatut]?.label || filtreStatut }),
                search && t('liste.export.recherche', { search }),
              ].filter(Boolean).join(' · ') || t('liste.export.tous')}
            />
          </Col>
        </Row>
      </div>

      <div className="cb-card" style={{ padding: 0 }}>
        <Table
          className="cb-table"
          dataSource={grouped}
          columns={colsSinistre}
          rowKey="key"
          loading={loading}
          scroll={{ x: 900 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('liste.vide')} /> }}
          expandable={{
            defaultExpandAllRows: true,
            expandedRowRender: (record) => (
              <Table
                size="small"
                dataSource={record.dossiers}
                columns={columns}
                rowKey="dossierTrackingId"
                pagination={false}
                style={{ marginLeft: 48, background: '#fafafa' }}
              />
            ),
            rowExpandable: (r) => r.dossiers.length > 0,
            expandIcon: ({ expanded, onExpand, record }) => (
              <RightOutlined
                rotate={expanded ? 90 : 0}
                onClick={(e) => onExpand(record, e)}
                style={{ color: GREEN, cursor: 'pointer', transition: 'transform 0.2s' }}
              />
            ),
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: () => <span style={{ fontSize: 13, color: '#6b7280' }}>{t('liste.totaux', { count: grouped.length, nbDossiers: filtered.length, context: new Intl.PluralRules(localeIntl()).select(filtered.length) === 'one' ? 'unDossier' : 'plusieursDossiers' })}</span>,
            onChange: (p) => { setPagination(prev => ({ ...prev, current: p })); load(p - 1); },
            hideOnSinglePage: !!sinistreFiltre,
          }}
        />
      </div>

      <NouveauDossierModal
        open={modalOpen}
        defaultSinistreId={defaultSinistreId}
        defaultVictimeId={defaultVictimeId}
        onClose={() => {
          setModalOpen(false);
          setDefaultSinistreId(null);
          setDefaultVictimeId(null);
        }}
        onCreated={(d) => {
          load(0);
          setDefaultSinistreId(null);
          setDefaultVictimeId(null);
          if (d?.dossierTrackingId) navigate(`/reclamations/${d.dossierTrackingId}`);
        }}
      />
    </Layout>
  );
}
